/**
 * File: contractsService.ts
 * Path: apps/admin/src/shared/services/contractsService.ts
 * Description: Every read and write the Contracts pages make.
 *
 * The admin writes drafts only: create, edit, withdraw a sent one, notes,
 * delete a never-sent one. Sending, signing and countersigning go through
 * the contract callables, which freeze the text, render and store the PDFs
 * and record the evidence (apps/server/src/features/contracts).
 *
 * A contract's reference is IDK-<client number>-<NN>: the client's contracts
 * in order (clients/data/clientNumbering). It is given when the draft is
 * created for a numbered client, or later with assignReference; until then
 * the reference is empty and the server will not send it. Firestore is
 * taken from a getter so the rules tests run these exact writes against the
 * emulator (tests/rules/contracts.rules.test.ts).
 */

import {
  auth,
  CONTRACT_COLLECTIONS,
  CONTRACT_SETTINGS_DOC,
  CONTRACTS_LIST_LIMIT,
  db,
  FunctionsService,
  toContract,
  toContractSettings,
  toHistoricInvoiceLink,
  toPaymentDueDate,
  toPaymentReceipt,
  type Contract,
  type ContractContent,
  type ContractImportSource,
  type ContractDeleteResult,
  type ContractLinkPurpose,
  type ContractSettings,
  type ContractStatus,
  type HistoricInvoiceLink,
  type LinkInvoicesResult,
  type Allocation,
  type ReceiptBookingResult,
  type PaymentDueDate,
  type PaymentReceipt,
  type StoredContractFile,
} from "@idk/config";
import { toPaymentReminder, type PaymentReminder } from "../utils/paymentRemindersUtils";
import {
  collection,
  collectionGroup,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  limit,
  orderBy,
  query,
  runTransaction,
  serverTimestamp,
  setDoc,
  updateDoc,
  where,
  type Firestore,
} from "firebase/firestore";
import {
  claimContractNumber,
  readClientInTx,
} from "./clientNumberingService";

const CONTRACTS = CONTRACT_COLLECTIONS.contracts;

/** An old signed contract being brought in; `pdf` is the signed file, base64. */
export interface ImportSignedInput {
  clientId: string;
  /** The client's job this is for (clients/<id>/projects/<jobId>), or null. */
  jobId: string | null;
  content: ContractContent;
  signedOn: string;
  source: ContractImportSource;
  clientSignerName: string;
  providerSignerName: string;
  notes: string;
  pdf: string;
}

/** What a new draft starts from. */
export interface NewContractInput {
  content: ContractContent;
  notes?: string;
  clientId?: string | null;
  leadId?: string | null;
  parentContractId?: string | null;
  /** The saved quote it starts from (quotes/{id}). */
  quoteId?: string | null;
  /** The client's job it is for (clients/<id>/projects/<jobId>). */
  jobId?: string | null;
}

export interface DraftPatch {
  content?: ContractContent;
  notes?: string;
  clientId?: string | null;
  leadId?: string | null;
}

export interface ContractEvent {
  id: string;
  type: string;
  at: number;
  detail: Record<string, unknown>;
}

export interface IssuedLink {
  url: string;
  purpose: ContractLinkPurpose;
  expiresAt: number;
  status: ContractStatus;
  reference: string;
}

export interface ContractPdf {
  filename: string;
  sha256: string;
  base64: string;
  stored: boolean;
}

/** Firestore rejects undefined; a content object built from a form can hold it. */
const plain = <T>(value: T): T => JSON.parse(JSON.stringify(value)) as T;

const byNewest = (a: Contract, b: Contract) =>
  (b.updatedAt ?? 0) - (a.updatedAt ?? 0);

/** Every contract's payments, by contract id (see allPayments). */
export interface AllContractPayments {
  receipts: Record<string, PaymentReceipt[]>;
  dueDates: Record<string, PaymentDueDate[]>;
  reminders: Record<string, PaymentReminder[]>;
}

export const createContractsRepo = (
  getDb: () => Firestore,
  getUid: () => string,
) => ({
  list: async (): Promise<Contract[]> => {
    const snap = await getDocs(
      query(
        collection(getDb(), CONTRACTS),
        orderBy("updatedAt", "desc"),
        limit(CONTRACTS_LIST_LIMIT),
      ),
    );
    return snap.docs.map((d) => toContract(d.id, d.data()));
  },

  /** A client's or a lead's contracts, newest first. */
  listFor: async (
    field: "clientId" | "leadId" | "jobId",
    id: string,
  ): Promise<Contract[]> => {
    // Equality only, sorted here: no composite index needed.
    const snap = await getDocs(
      query(collection(getDb(), CONTRACTS), where(field, "==", id), limit(50)),
    );
    return snap.docs.map((d) => toContract(d.id, d.data())).sort(byNewest);
  },

  get: async (id: string): Promise<Contract | null> => {
    const snap = await getDoc(doc(getDb(), CONTRACTS, id));
    const data = snap.data();
    return data ? toContract(snap.id, data) : null;
  },

  events: async (id: string): Promise<ContractEvent[]> => {
    const snap = await getDocs(
      query(
        collection(getDb(), CONTRACTS, id, CONTRACT_COLLECTIONS.events),
        orderBy("at", "desc"),
        limit(100),
      ),
    );
    return snap.docs.map((d) => {
      const { type, at, ...detail } = d.data();
      return {
        id: d.id,
        type: typeof type === "string" ? type : "",
        at: typeof at === "number" ? at : 0,
        detail,
      };
    });
  },

  /** What has been marked received on a contract, one per payment. */
  receipts: async (contractId: string): Promise<PaymentReceipt[]> => {
    const snap = await getDocs(
      collection(getDb(), CONTRACTS, contractId, CONTRACT_COLLECTIONS.receipts),
    );
    return snap.docs.flatMap((d) => {
      const receipt = toPaymentReceipt(d.id, d.data());
      return receipt ? [receipt] : [];
    });
  },

  /**
   * Every contract's receipts, due dates and reminders in three reads (one
   * collection-group query each) instead of three per contract. Keyed by
   * contract id. The pages that add up many contracts use this.
   */
  allPayments: async (): Promise<AllContractPayments> => {
    const [receipts, dueDates, reminders] = await Promise.all(
      [CONTRACT_COLLECTIONS.receipts, CONTRACT_COLLECTIONS.dueDates, CONTRACT_COLLECTIONS.reminders].map((name) =>
        getDocs(collectionGroup(getDb(), name)),
      ),
    );
    const group = <T,>(snap: typeof receipts, parse: (id: string, data: Record<string, unknown>) => T | null) => {
      const byContract: Record<string, T[]> = {};
      for (const d of snap.docs) {
        // Only the ones under a contract: the name could be reused elsewhere.
        if (d.ref.parent.parent?.parent.id !== CONTRACTS) continue;
        const parsed = parse(d.id, d.data());
        if (!parsed) continue;
        (byContract[d.ref.parent.parent.id] ??= []).push(parsed);
      }
      return byContract;
    };
    return {
      receipts: group(receipts, toPaymentReceipt),
      dueDates: group(dueDates, toPaymentDueDate),
      reminders: group(reminders, toPaymentReminder),
    };
  },

  /**
   * Which books invoice each brought-in contract's payments were billed on,
   * by contract id (one collection-group read). Links only: the figures are
   * read from the books.
   */
  allInvoiceLinks: async (): Promise<Record<string, HistoricInvoiceLink[]>> => {
    const snap = await getDocs(collectionGroup(getDb(), CONTRACT_COLLECTIONS.invoiceLinks));
    const byContract: Record<string, HistoricInvoiceLink[]> = {};
    for (const d of snap.docs) {
      if (d.ref.parent.parent?.parent.id !== CONTRACTS) continue;
      const link = toHistoricInvoiceLink(d.id, d.data());
      if (link) (byContract[d.ref.parent.parent.id] ??= []).push(link);
    }
    return byContract;
  },

  /** Pairs a brought-in contract's paid payments with the books' invoices; `apply` false only reports. */
  /** A dry run (apply false) shows each change; on apply, `choices` (payment reference → invoice, or null) override the rule's own pick. */
  linkInvoices: (contractId: string, apply: boolean, choices?: Record<string, string | null>) =>
    FunctionsService.call<{ contractId: string; apply: boolean; choices?: Record<string, string | null> }, LinkInvoicesResult>(
      "linkContractInvoices",
      { contractId, apply, ...(choices ? { choices } : {}) },
    ),

  /** Due dates set by hand on a contract's payments, one per payment. */
  dueDates: async (contractId: string): Promise<PaymentDueDate[]> => {
    const snap = await getDocs(
      collection(getDb(), CONTRACTS, contractId, CONTRACT_COLLECTIONS.dueDates),
    );
    return snap.docs.flatMap((d) => {
      const due = toPaymentDueDate(d.id, d.data());
      return due ? [due] : [];
    });
  },

  /** Sets (or changes) the day one payment is due; a plain YYYY-MM-DD. */
  setDueDate: (contractId: string, reference: string, dueOn: string) =>
    setDoc(
      doc(getDb(), CONTRACTS, contractId, CONTRACT_COLLECTIONS.dueDates, reference),
      { reference, dueOn, setBy: getUid(), updatedAt: serverTimestamp() },
    ),

  /** Every WhatsApp reminder logged for a contract's payments. */
  paymentReminders: async (contractId: string): Promise<PaymentReminder[]> => {
    const snap = await getDocs(
      collection(getDb(), CONTRACTS, contractId, CONTRACT_COLLECTIONS.reminders),
    );
    return snap.docs.flatMap((d) => {
      const reminder = toPaymentReminder(d.id, d.data());
      return reminder ? [reminder] : [];
    });
  },

  /** One line of history: a reminder for this payment went out on `sentOn` (YYYY-MM-DD). */
  logPaymentReminder: (contractId: string, reference: string, sentOn: string) =>
    setDoc(
      doc(collection(getDb(), CONTRACTS, contractId, CONTRACT_COLLECTIONS.reminders)),
      { reference, sentOn, sentBy: getUid(), createdAt: serverTimestamp() },
    ),

  /** Goes back to the worked-out date (or none). */
  clearDueDate: (contractId: string, reference: string) =>
    deleteDoc(
      doc(getDb(), CONTRACTS, contractId, CONTRACT_COLLECTIONS.dueDates, reference),
    ),

  /**
   * Ticks a scheduled payment off. The server saves the receipt and, for a
   * contract made here, posts the money to the company books in the same
   * call (so the books and the CRM can't disagree).
   */
  /**
   * Books the money too: against the payment's invoice, or held as the
   * client's credit until it has one. `split` spreads it over several
   * invoices (whole cents), the rest held as credit.
   */
  markReceived: (
    contractId: string,
    receipt: { reference: string; amount: number; receivedOn: string; split?: { allocations: Allocation[]; creditCents: number } },
  ) =>
    FunctionsService.call<{ contractId: string } & typeof receipt, ReceiptBookingResult>("markContractPayment", { contractId, ...receipt }),

  /** Undoes a tick made by mistake: the receipt goes and its journal is reversed. */
  undoReceived: (contractId: string, reference: string) =>
    FunctionsService.call<{ contractId: string; reference: string }, { reversed: boolean }>(
      "undoContractPayment",
      { contractId, reference },
    ),

  /**
   * A new draft. For a client with a number (or a lead already won, whose
   * client shares its id) it takes the client's next contract number and
   * its reference in the same transaction; otherwise it waits for one.
   */
  create: async (input: NewContractInput): Promise<string> => {
    const store = getDb();
    const ref = doc(collection(store, CONTRACTS));
    await runTransaction(store, async (tx) => {
      const candidate = input.clientId ?? input.leadId ?? null;
      const client = candidate
        ? await readClientInTx(tx, store, candidate)
        : null;
      const numbering = client
        ? claimContractNumber(tx, store, client, ref.id)
        : null;
      tx.set(ref, {
        reference: numbering?.reference ?? "",
        ...(numbering
          ? {
              clientNumber: numbering.clientNumber,
              contractNumber: numbering.contractNumber,
            }
          : {}),
        status: "draft",
        clientId: client?.id ?? input.clientId ?? null,
        leadId: input.leadId ?? null,
        parentContractId: input.parentContractId ?? null,
        quoteId: input.quoteId ?? null,
        ...(input.jobId ? { jobId: input.jobId } : {}),
        content: plain(input.content),
        notes: input.notes ?? "",
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        createdBy: getUid(),
      });
    });
    return ref.id;
  },

  /**
   * Gives a draft that has no reference yet its place among `clientId`'s
   * contracts, and links it to that client. Refused for a client without a
   * number: run "Number existing clients" first.
   */
  assignReference: async (
    contractId: string,
    clientId: string,
  ): Promise<string> => {
    const store = getDb();
    const contractRef = doc(store, CONTRACTS, contractId);
    return runTransaction(store, async (tx) => {
      const snap = await tx.get(contractRef);
      const client = await readClientInTx(tx, store, clientId);
      const data = snap.data();
      if (!data) throw new Error("This contract doesn't exist any more.");
      const contract = toContract(snap.id, data);
      if (contract.status !== "draft" || contract.reference) {
        throw new Error("This contract already has its reference.");
      }
      if (!client) throw new Error("That client doesn't exist any more.");
      const numbering = claimContractNumber(tx, store, client, contractId);
      if (!numbering) {
        throw new Error(
          `${client.name} has no client number yet. Run "Number existing clients" on the Clients page first.`,
        );
      }
      tx.update(contractRef, { ...numbering, updatedAt: serverTimestamp() });
      return numbering.reference;
    });
  },

  /** Terms, notes and CRM links of a draft. The rules refuse it once sent. */
  updateDraft: (id: string, patch: DraftPatch) =>
    updateDoc(doc(getDb(), CONTRACTS, id), {
      ...plain(patch),
      updatedAt: serverTimestamp(),
    }),

  /** Internal notes: allowed at every stage. */
  updateNotes: (id: string, notes: string) =>
    updateDoc(doc(getDb(), CONTRACTS, id), {
      notes,
      updatedAt: serverTimestamp(),
    }),

  /** Back to draft: its signing link stops working; sending again issues a new one. */
  withdraw: (id: string) =>
    updateDoc(doc(getDb(), CONTRACTS, id), {
      status: "draft",
      updatedAt: serverTimestamp(),
    }),

  getSettings: async (): Promise<ContractSettings> => {
    const snap = await getDoc(
      doc(getDb(), CONTRACT_COLLECTIONS.settings, CONTRACT_SETTINGS_DOC),
    );
    return toContractSettings(snap.data() ?? null);
  },

  saveSettings: (settings: ContractSettings) =>
    setDoc(doc(getDb(), CONTRACT_COLLECTIONS.settings, CONTRACT_SETTINGS_DOC), {
      ...plain(settings),
      updatedAt: serverTimestamp(),
    }),

  // ─── Through the server ───────────────────────────────────────────────

  /** What deleting an unsigned contract would take with it, or why it can't be deleted. Changes nothing. */
  previewDelete: (contractId: string) =>
    FunctionsService.call<{ contractId: string; apply: boolean }, ContractDeleteResult>(
      "deleteContract",
      { contractId, apply: false },
    ),

  /** Deletes an unsigned contract with everything linked to it. Signed ones are refused. */
  remove: (contractId: string) =>
    FunctionsService.call<{ contractId: string; apply: boolean }, ContractDeleteResult>(
      "deleteContract",
      { contractId, apply: true },
    ),

  /** Hides a signed contract from the lists (or brings it back). A display flag only. */
  archive: (contractId: string, archived: boolean) =>
    FunctionsService.call<{ contractId: string; archived: boolean }, { archived: boolean }>(
      "archiveContract",
      { contractId, archived },
    ),

  /** Sends a draft (freezing it) or re-issues a link; returns the URL to share. */
  issueLink: (contractId: string, purpose: ContractLinkPurpose) =>
    FunctionsService.call<
      { contractId: string; purpose: ContractLinkPurpose },
      IssuedLink
    >("issueContractLink", { contractId, purpose }),

  /** The draft PDF for the form as it is being edited (not saved), drawn by the server. */
  preview: (contractId: string, content: ContractContent) =>
    FunctionsService.call<
      { contractId: string; content: ContractContent },
      { filename: string; base64: string }
    >("previewContractPdf", { contractId, content }),

  pdf: (contractId: string) =>
    FunctionsService.call<{ contractId: string }, ContractPdf>(
      "getContractPdf",
      { contractId },
    ),

  /** Both drawn signatures as PNG data URLs (null where nobody has signed). */
  signatures: (contractId: string) =>
    FunctionsService.call<
      { contractId: string },
      { client: string | null; provider: string | null }
    >("getContractSignatures", { contractId }),

  /** Brings in a contract signed elsewhere: the old signed PDF is the proof. */
  importSigned: (input: ImportSignedInput) =>
    FunctionsService.call<
      ImportSignedInput,
      { contractId: string; reference: string; signedPdf: StoredContractFile }
    >("importSignedContract", input),

  countersign: (contractId: string, signature: string) =>
    FunctionsService.call<
      { contractId: string; signature: string },
      { status: ContractStatus; signedPdf: StoredContractFile }
    >("countersignContract", { contractId, signature }),
});

/** The signed-in admin, stamped on each draft as createdBy (checked by the rules). */
const currentUid = (): string => auth?.currentUser?.uid ?? "";

export const contractsRepo = createContractsRepo(() => db, currentUid);

export type ContractsRepo = ReturnType<typeof createContractsRepo>;
